import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Plus, ChevronDown, Building2, FileText, Receipt, Send, FolderKanban,
  FileStack, Settings, Zap, AppWindow, Home, Sparkles, LogOut,
} from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "../lib/utils";
import { listCompanies } from "../lib/api";
import { CompanyWizard } from "./CompanyWizard";
import { useAuth } from "../lib/auth";

// A company's sub-menu (same structure for every company - the operations available)
const COMPANY_MENU = [
  {
    label: "שירותי שליחה",
    items: [
      {
        label: "שליחת מסמכים",
        icon: Send,
        isGroup: true,
        children: [
          { to: "/S.ADMIN/invoices", label: "שליחת חשבון עסקה", icon: FileText, testid: "nav-invoices" },
          { to: "/S.ADMIN/tax-invoices", label: "שליחת חשבונית מס", icon: Receipt, testid: "nav-tax-invoices" },
        ],
      },
      { to: "/S.ADMIN/history", label: "היסטוריה", icon: FileStack, testid: "nav-history" },
    ],
  },
  {
    label: "אוטומציות",
    items: [
      { to: "#automations", label: "כל האוטומציות", icon: Zap, disabled: true },
    ],
  },
  {
    label: "אפליקציות",
    items: [
      { to: "#apps", label: "כל האפליקציות", icon: AppWindow, disabled: true },
    ],
  },
];

function NavGroup({ group, currentPath }) {
  const hasActiveChild = group.children.some((c) => {
    const cPath = (c.to || "").split("?")[0];
    return cPath === currentPath;
  });
  const [open, setOpen] = useState(hasActiveChild);
  useEffect(() => { if (hasActiveChild) setOpen(true); }, [hasActiveChild]);
  const Icon = group.icon;
  return (
    <div>
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          hasActiveChild ? "text-brand" : "text-stone-700 hover:bg-stone-100"
        )}
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-right">{group.label}</span>
        <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="mr-4 mt-1 space-y-0.5 border-r-2 border-brand/20 pr-2">
          {group.children.map((l) => {
            const CIcon = l.icon;
            const cPath = (l.to || "").split("?")[0];
            const active = currentPath === cPath;
            return (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "flex items-center gap-2 px-2 py-2 rounded-lg text-xs font-medium transition-colors",
                  active ? "bg-brand-50 text-brand font-semibold" : "text-stone-600 hover:bg-stone-100"
                )}
                data-testid={l.testid}
              >
                <CIcon className="h-3.5 w-3.5" />
                <span>{l.label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NavItem({ item, currentPath }) {
  const Icon = item.icon;
  const itemPath = (item.to || "").split("?")[0];
  const active = currentPath === itemPath;
  if (item.disabled) {
    return (
      <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-stone-400 cursor-not-allowed">
        <Icon className="h-4 w-4" />
        <span>{item.label}</span>
        <span className="ml-auto text-[9px] bg-stone-100 px-1.5 py-0.5 rounded">בקרוב</span>
      </div>
    );
  }
  return (
    <Link
      to={item.to}
      data-testid={item.testid}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
        active ? "bg-brand text-white shadow-sm" : "text-stone-700 hover:bg-stone-100"
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{item.label}</span>
    </Link>
  );
}

function CompanyBlock({ company, currentPath, defaultOpen, colorIndex = 0 }) {
  const [open, setOpen] = useState(defaultOpen);

  // Rotating accent palette so each company gets its own identity
  const PALETTES = [
    { ring: "ring-emerald-200", bar: "bg-emerald-500", headerBg: "bg-emerald-50", headerText: "text-emerald-900", avatarBg: "bg-emerald-500" },
    { ring: "ring-blue-200", bar: "bg-blue-500", headerBg: "bg-blue-50", headerText: "text-blue-900", avatarBg: "bg-blue-500" },
    { ring: "ring-amber-200", bar: "bg-amber-500", headerBg: "bg-amber-50", headerText: "text-amber-900", avatarBg: "bg-amber-500" },
    { ring: "ring-purple-200", bar: "bg-purple-500", headerBg: "bg-purple-50", headerText: "text-purple-900", avatarBg: "bg-purple-500" },
    { ring: "ring-rose-200", bar: "bg-rose-500", headerBg: "bg-rose-50", headerText: "text-rose-900", avatarBg: "bg-rose-500" },
  ];
  const palette = PALETTES[colorIndex % PALETTES.length];

  // Channel badges (WhatsApp / Email / SMS) - same for all companies
  const channels = company.channels || [];
  const channelBadges = [];
  if (channels.includes("whatsapp")) channelBadges.push({ label: "וואטסאפ", cls: "bg-green-100 text-green-700" });
  if (channels.includes("email")) channelBadges.push({ label: "אימייל", cls: "bg-blue-100 text-blue-700" });
  if (channels.includes("sms")) channelBadges.push({ label: "SMS", cls: "bg-amber-100 text-amber-700" });

  // Only 3 document types currently active (bold, clickable).
  // Others are displayed dimmed & disabled.
  const incomeDocs = [
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=tax_invoice`, label: "חשבונית מס", testid: "nav-tax-invoice", active: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=tax_invoice_receipt`, label: "חשבונית מס / קבלה", testid: "nav-tax-receipt", active: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=receipt`, label: "קבלה", testid: "nav-receipt", disabled: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=credit_invoice`, label: "חשבונית זיכוי", testid: "nav-credit", disabled: true },
  ];
  const operationsDocs = [
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=price_quote`, label: "הצעת מחיר", testid: "nav-quote", disabled: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=transaction_account`, label: "חשבון עסקה", testid: "nav-transaction", active: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=work_order`, label: "הזמנת עבודה", testid: "nav-work-order", disabled: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=delivery_note`, label: "תעודת משלוח", testid: "nav-delivery", disabled: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=return_note`, label: "תעודת החזרה", testid: "nav-return", disabled: true },
    { to: `/S.ADMIN/invoices?company=${company.id}&doc=purchase_order`, label: "הזמנת רכש", testid: "nav-purchase", disabled: true },
  ];
  // NEW: Sent documents — 3 active types with their history + actions
  const sentDocs = [
    { to: `/S.ADMIN/sent?company=${company.id}&doc=transaction_account`, label: "חשבון עסקה", testid: "nav-sent-transaction", active: true },
    { to: `/S.ADMIN/sent?company=${company.id}&doc=tax_invoice`, label: "חשבונית מס", testid: "nav-sent-tax", active: true },
    { to: `/S.ADMIN/sent?company=${company.id}&doc=tax_invoice_receipt`, label: "חשבונית מס / קבלה", testid: "nav-sent-tax-receipt", active: true },
  ];
  const otherLinks = [
    { to: `/S.ADMIN/history?company=${company.id}`, label: "היסטוריה", icon: FileStack, testid: "nav-history" },
  ];

  return (
    <div
      className={cn(
        "relative bg-white rounded-xl border-2 overflow-hidden transition-all",
        open ? `${palette.ring} ring-2 border-stone-200 shadow-md` : "border-stone-200 hover:border-stone-300 shadow-sm"
      )}
      data-testid={`company-card-${company.id}`}
    >
      {/* Left colored accent bar */}
      <div className={cn("absolute right-0 top-0 bottom-0 w-1", palette.bar)} />

      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-2.5 px-3 py-3 font-bold text-[13px] transition-colors",
          open ? palette.headerBg : "hover:bg-stone-50"
        )}
        data-testid={`company-toggle-${company.id}`}
      >
        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-black flex-shrink-0 shadow-sm", palette.avatarBg)}>
          {(company.name || "?").charAt(0)}
        </div>
        <div className="flex-1 flex flex-col items-end min-w-0">
          <span className={cn("text-right truncate w-full text-[14px]", open ? palette.headerText : "text-stone-800")}>
            {company.name}
          </span>
          {channelBadges.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap justify-end" data-testid={`company-channels-${company.id}`}>
              {channelBadges.map((b) => (
                <span key={b.label} className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-semibold", b.cls)}>
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown className={cn("h-4 w-4 transition-transform text-stone-500", open ? "rotate-180" : "")} />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-2 bg-white border-t border-stone-100">
          {/* Income Documents - collapsible */}
          <CollapsibleSection
            title="מסמכי הכנסות"
            icon="📥"
            color="text-emerald-600"
            items={incomeDocs}
            currentPath={currentPath}
          />

          {/* Operations Documents - collapsible */}
          <CollapsibleSection
            title="מסמכי ניהול שוטף"
            icon="📋"
            color="text-blue-600"
            items={operationsDocs}
            currentPath={currentPath}
          />

          {/* Sent documents - collapsible */}
          <CollapsibleSection
            title="מסמכים שנשלחו"
            icon="📤"
            color="text-purple-600"
            items={sentDocs}
            currentPath={currentPath}
          />

          {/* Other */}
          <div>
            <p className="px-3 mb-1 text-[10px] uppercase tracking-widest font-bold text-stone-400">
              כללי
            </p>
            <div className="space-y-0.5">
              {otherLinks.map((item) => (
                <NavItem key={item.to} item={item} currentPath={currentPath} />
              ))}
            </div>
          </div>

          {/* Per-company Automations Hub entry */}
          <Link
            to={`/S.ADMIN/automations?company=${company.id}`}
            data-testid={`company-automations-${company.id}`}
            className="mt-1 relative flex items-center gap-2 px-3 py-2.5 rounded-lg text-[12px] font-bold bg-gradient-to-l from-violet-600 to-fuchsia-600 text-white shadow-sm hover:shadow-md hover:brightness-110 transition-all overflow-hidden"
          >
            <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="flex-1 text-right">אוטומציות נוספות</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-white/25">חדש</span>
          </Link>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, icon, color, items, currentPath }) {
  // Default: collapsed. Auto-expand if any child is active.
  const hasActive = items.some((d) => {
    const p = (d.to || "").split("?")[0];
    const params = new URLSearchParams((d.to || "").split("?")[1] || "");
    return p === currentPath && new URLSearchParams(window.location.search).get("doc") === params.get("doc");
  });
  const [open, setOpen] = useState(hasActive);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);

  return (
    <div>
      <button type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-widest font-bold transition-colors",
          color,
          open ? "bg-stone-50" : "hover:bg-stone-50"
        )}
      >
        <span>{icon}</span>
        <span className="flex-1 text-right">{title}</span>
        <ChevronDown className={cn("h-3 w-3 transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="space-y-0.5 mt-1 mr-2">
          {items.map((d) => (
            <DocLink
              key={d.to}
              to={d.to}
              label={d.label}
              testid={d.testid}
              disabled={d.disabled}
              active={d.active}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DocLink({ to, label, testid, disabled, active: markedActive, currentPath }) {
  const itemPath = (to || "").split("?")[0];
  const params = new URLSearchParams((to || "").split("?")[1] || "");
  const docType = params.get("doc");
  const isActive = currentPath === itemPath && new URLSearchParams(window.location.search).get("doc") === docType;

  if (disabled) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] text-stone-400 opacity-50 cursor-not-allowed"
        title="בקרוב"
        data-testid={testid}
      >
        <span className="h-1 w-1 rounded-full bg-current opacity-30" />
        <span>{label}</span>
        <span className="ml-auto text-[9px] bg-stone-100 px-1 py-0.5 rounded text-stone-400">בקרוב</span>
      </div>
    );
  }
  return (
    <Link
      to={to}
      data-testid={testid}
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-md text-[12px] transition-colors",
        isActive
          ? "bg-brand-50 text-brand font-bold"
          : markedActive
          ? "text-stone-900 font-bold hover:bg-stone-100"
          : "text-stone-700 hover:bg-stone-100"
      )}
    >
      <span className="h-1 w-1 rounded-full bg-current opacity-50" />
      <span>{label}</span>
    </Link>
  );
}

export function ShanySidebar() {
  const location = useLocation();
  const [companies, setCompanies] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const data = await listCompanies();
      setCompanies(data.companies || []);
    } catch {
      setCompanies([]);
    }
  };

  useEffect(() => { load(); }, []);

  const isOnDashboard = location.pathname === "/S.ADMIN";

  // White-label: For an "owner" with a single company, show THEIR logo + name
  // instead of the FlowUp branding. Super admins keep the FlowUp branding so
  // they know they're in the platform admin.
  const isOwner = user?.role === "owner";
  const ownCompany = isOwner ? companies.find((c) => c.id === user?.company_id) || companies[0] : null;
  const showOwnBrand = !!(ownCompany && (ownCompany.logo_url || ownCompany.name));

  return (
    <>
      <aside
        className="fixed right-0 top-0 h-screen w-64 bg-white border-l border-border hidden lg:flex flex-col overflow-y-auto"
        data-testid="shany-sidebar"
      >
        <div className="px-4 pt-4 pb-3 border-b border-border">
          <Link to="/S.ADMIN" className="flex flex-col items-center group" data-testid="sidebar-logo">
            {showOwnBrand ? (
              ownCompany.logo_url ? (
                <>
                  <div className="w-full max-w-[140px] flex items-center justify-center">
                    <img
                      src={ownCompany.logo_url}
                      alt={ownCompany.name}
                      className="w-full h-auto max-h-24 object-contain"
                      data-testid="sidebar-company-logo"
                    />
                  </div>
                  <p className="text-xs text-stone-700 font-bold mt-2 text-center truncate w-full">
                    {ownCompany.display_name || ownCompany.name}
                  </p>
                </>
              ) : (
                <>
                  <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-brand to-brand-700 text-white flex items-center justify-center font-heading font-bold text-2xl shadow-md">
                    {(ownCompany.name || "?").trim().charAt(0).toUpperCase()}
                  </div>
                  <p className="text-sm text-stone-800 font-bold mt-2 text-center truncate w-full">
                    {ownCompany.display_name || ownCompany.name}
                  </p>
                </>
              )
            ) : (
              <>
                <div className="w-full max-w-[160px] flex items-center justify-center">
                  <img src="/logo.png" alt="FlowUp" className="w-full h-auto object-contain" />
                </div>
                <p className="text-[11px] text-stone-600 uppercase tracking-[0.25em] font-semibold mt-1">
                  Admin Panel
                </p>
              </>
            )}
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-3">
          {/* Top link to dashboard */}
          <Link
            to="/S.ADMIN"
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              isOnDashboard ? "bg-brand text-white shadow-sm" : "text-stone-700 hover:bg-stone-100"
            )}
            data-testid="nav-dashboard"
          >
            <Home className="h-4 w-4" />
            <span>דשבורד ראשי</span>
          </Link>

          {/* New company button */}
          <button type="button"
            onClick={() => setWizardOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl bg-gradient-to-br from-brand to-brand-700 text-white font-bold text-sm hover:shadow-lg transition-all"
            data-testid="sidebar-new-company"
          >
            <Plus className="h-4 w-4" />
            הקמת חברה חדשה
          </button>

          {/* Companies list */}
          <div className="pt-1">
            <p className="px-3 mb-2 text-[10px] uppercase tracking-widest font-bold text-stone-400 flex items-center justify-between">
              <span>{user?.role === "super_admin" ? "כל החברות" : "החברות שלי"}</span>
              <span className="text-[10px] text-stone-500">{companies.length}</span>
            </p>
            {companies.length === 0 ? (
              <p className="px-3 text-xs text-stone-400 italic py-2">
                אין עדיין חברות - לחצי על "הקמת חברה חדשה"
              </p>
            ) : (
              <div className="space-y-3">
                {companies.map((c, i) => (
                  <CompanyBlock
                    key={c.id}
                    company={c}
                    currentPath={location.pathname}
                    defaultOpen={i === 0 && !isOnDashboard}
                    colorIndex={i}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="pt-1 space-y-1">
            <Link
              to="/S.ADMIN/settings"
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                location.pathname === "/S.ADMIN/settings" ? "bg-stone-100 text-stone-900" : "text-stone-600 hover:bg-stone-100"
              )}
              data-testid="nav-settings"
            >
              <Settings className="h-4 w-4" />
              <span>הגדרות כלליות</span>
            </Link>

            {/* Sign out */}
            <button type="button"
              onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700"
              data-testid="nav-logout"
            >
              <LogOut className="h-4 w-4" />
              <span>יציאה</span>
            </button>
          </div>
        </nav>

        {!isOwner && (
          <div className="p-4 border-t border-border">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-3 border border-brand/10">
              <p className="text-[10px] text-stone-500 font-semibold uppercase tracking-wider">Powered by</p>
              <p className="text-sm font-heading font-bold text-brand mt-0.5">FlowUp</p>
            </div>
          </div>
        )}
      </aside>

      <CompanyWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        editing={null}
        onSaved={async () => { setWizardOpen(false); await load(); }}
      />
    </>
  );
}
